import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, AlertTriangle, FileText, Brain, Lock, Network, 
  ChevronRight, Check, X, RefreshCw, Search, Sparkles, Mic
} from 'lucide-react';
import { brandColors } from '../components/Brand';
import api from '../api/api';
import { toast } from 'sonner';
import { RiskMonitor, IntelligenceDashboard, WorkflowBuilder, CustomCommandsManager } from '../components/agentic';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { getAuthHeaders, user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Security state
  const [incidents, setIncidents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  
  // Contract state
  const [obligations, setObligations] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState([]);
  
  // Workflow state
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [workflowDraft, setWorkflowDraft] = useState(null);
  const [parsingWorkflow, setParsingWorkflow] = useState(false);
  
  // AI Agent state
  const [agentQuery, setAgentQuery] = useState('');
  const [agentResponse, setAgentResponse] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState('policy');
  const [queryingAgent, setQueryingAgent] = useState(false);
  
  // Knowledge state
  const [knowledgeGraph, setKnowledgeGraph] = useState(null);
  
  // Compliance report
  const [complianceReport, setComplianceReport] = useState(null);

  useEffect(() => {
    document.title = 'ReasonDMS | Admin';
    loadSecurityData();
    loadContractData();
  }, []);

  const loadSecurityData = async () => {
    try {
      const headers = getAuthHeaders();
      const [incidentsRes, alertsRes] = await Promise.all([
        api.getSecurityIncidents({}, headers),
        api.getSecurityAlerts(headers)
      ]);
      setIncidents(incidentsRes.data.incidents || []);
      setAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      console.log('Security data not available');
    }
  };

  const loadContractData = async () => {
    try {
      const headers = getAuthHeaders();
      const [obligationsRes, expiringRes] = await Promise.all([
        api.getContractObligations({}, headers),
        api.getExpiringContracts(30, headers)
      ]);
      setObligations(obligationsRes.data.obligations || []);
      setExpiringContracts(expiringRes.data.contracts || []);
    } catch (error) {
      console.log('Contract data not available');
    }
  };

  const handleParseWorkflow = async () => {
    if (!workflowDescription.trim()) {
      toast.error('Please enter a workflow description');
      return;
    }
    
    setParsingWorkflow(true);
    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      formData.append('description', workflowDescription);
      
      const response = await api.parseWorkflowDescription(formData, headers);
      
      if (response.data.success) {
        setWorkflowDraft(response.data);
        toast.success('Workflow parsed successfully!');
      } else {
        toast.error(response.data.error || 'Failed to parse workflow');
      }
    } catch (error) {
      toast.error('Failed to parse workflow');
    } finally {
      setParsingWorkflow(false);
    }
  };

  const handleActivateWorkflow = async () => {
    if (!workflowDraft?.draft_id) return;
    
    try {
      const headers = getAuthHeaders();
      const response = await api.activateWorkflowDraft(workflowDraft.draft_id, headers);
      
      if (response.data.success) {
        toast.success('Workflow activated successfully!');
        setWorkflowDraft(null);
        setWorkflowDescription('');
      } else {
        toast.error(response.data.error || 'Failed to activate workflow');
      }
    } catch (error) {
      const detail = error.response?.data?.error || error.response?.data?.detail || 'Failed to activate workflow';
      toast.error(detail);
    }
  };

  const handleQueryAgent = async () => {
    if (!agentQuery.trim()) {
      toast.error('Please enter a question');
      return;
    }
    
    setQueryingAgent(true);
    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      formData.append('question', agentQuery);
      
      let response;
      switch (selectedAgent) {
        case 'policy':
          response = await api.queryPolicyAgent(formData, headers);
          break;
        case 'audit':
          formData.append('time_range_hours', '168'); // 7 days
          response = await api.queryAuditAgent(formData, headers);
          break;
        case 'vendor':
          response = await api.queryVendorAgent(formData, headers);
          break;
        default:
          response = await api.queryPolicyAgent(formData, headers);
      }
      
      if (response.data.success) {
        setAgentResponse(response.data);
      } else {
        toast.error(response.data.error || 'Agent query failed');
      }
    } catch (error) {
      toast.error('Failed to query agent');
    } finally {
      setQueryingAgent(false);
    }
  };

  const loadKnowledgeGraph = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await api.getKnowledgeGraph({}, headers);
      setKnowledgeGraph(response.data);
    } catch (error) {
      toast.error('Failed to load knowledge graph');
    } finally {
      setLoading(false);
    }
  };

  const loadComplianceReport = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await api.getComplianceReport('monthly', headers);
      setComplianceReport(response.data.report);
    } catch (error) {
      toast.error('Failed to load compliance report');
    } finally {
      setLoading(false);
    }
  };

  const resolveIncident = async (incidentId) => {
    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      formData.append('resolution', 'Reviewed and resolved by admin');
      
      await api.resolveIncident(incidentId, formData, headers);
      toast.success('Incident resolved');
      loadSecurityData();
    } catch (error) {
      toast.error('Failed to resolve incident');
    }
  };

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: brandColors.primary }}>
            ReasonDMS {t('admin.title')}
          </h1>
          <p className="text-slate-600 mt-1">{t('admin.subtitle')}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500">Powered by</p>
          <p className="font-bold" style={{ color: brandColors.primary }}>REASON</p>
        </div>
      </div>

      <Tabs defaultValue="security" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            {t('admin.security')}
          </TabsTrigger>
          <TabsTrigger value="contracts" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {t('admin.contracts')}
          </TabsTrigger>
          <TabsTrigger value="workflows" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            {t('admin.aiWorkflows')}
          </TabsTrigger>
          <TabsTrigger value="voice" className="flex items-center gap-2" data-testid="voice-commands-tab">
            <Mic className="w-4 h-4" />
            Voice
          </TabsTrigger>
          <TabsTrigger value="agents" className="flex items-center gap-2">
            <Brain className="w-4 h-4" />
            AI Agents
          </TabsTrigger>
          <TabsTrigger value="knowledge" className="flex items-center gap-2">
            <Network className="w-4 h-4" />
            Knowledge
          </TabsTrigger>
        </TabsList>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-4">
          {/* AI-Powered Risk Monitor */}
          <RiskMonitor headers={getAuthHeaders()} />
          
          {/* Legacy Security Incidents */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Security Incidents
                </CardTitle>
                <CardDescription>
                  Anomalous behavior and security events
                </CardDescription>
              </CardHeader>
              <CardContent>
                {incidents.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Shield className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No security incidents</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {incidents.map((incident) => (
                      <div 
                        key={incident.id} 
                        className="p-3 bg-red-50 border border-red-200 rounded-lg"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-red-800">
                              {incident.incident_type}
                            </p>
                            <p className="text-sm text-red-600">
                              Risk Score: {incident.risk_score}
                            </p>
                          </div>
                          {incident.status === 'pending_review' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => resolveIncident(incident.id)}
                            >
                              Resolve
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-orange-500" />
                  Security Alerts
                </CardTitle>
                <CardDescription>
                  Notifications requiring attention
                </CardDescription>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Check className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No pending alerts</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.map((alert) => (
                      <div 
                        key={alert.id} 
                        className={`p-3 rounded-lg border ${
                          alert.priority === 'urgent' 
                            ? 'bg-red-50 border-red-200' 
                            : 'bg-orange-50 border-orange-200'
                        }`}
                      >
                        <p className="font-medium">{alert.type}</p>
                        <p className="text-sm text-slate-600">{alert.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Compliance Report</CardTitle>
              <CardDescription>
                Monthly compliance and audit summary
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!complianceReport ? (
                <Button onClick={loadComplianceReport} disabled={loading}>
                  {loading ? 'Loading...' : 'Generate Report'}
                </Button>
              ) : (
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {complianceReport.total_actions}
                    </p>
                    <p className="text-sm text-slate-600">Total Actions</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {complianceReport.anomalies_detected}
                    </p>
                    <p className="text-sm text-slate-600">Anomalies</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-orange-600">
                      {complianceReport.security_incidents}
                    </p>
                    <p className="text-sm text-slate-600">Incidents</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-600">
                      {complianceReport.high_risk_incidents}
                    </p>
                    <p className="text-sm text-slate-600">High Risk</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contracts Tab */}
        <TabsContent value="contracts" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Contract Obligations</CardTitle>
                <CardDescription>
                  Tracked obligations from analyzed contracts
                </CardDescription>
              </CardHeader>
              <CardContent>
                {obligations.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">
                    No obligations tracked. Analyze a contract to see obligations.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {obligations.slice(0, 10).map((obligation, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border ${
                          obligation.priority === 'Critical' 
                            ? 'border-red-200 bg-red-50' 
                            : 'border-slate-200 bg-slate-50'
                        }`}
                      >
                        <div className="flex justify-between">
                          <span className="font-medium">{obligation.party}</span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            obligation.status === 'pending' 
                              ? 'bg-yellow-100 text-yellow-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {obligation.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          {obligation.description}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Expiring Contracts</CardTitle>
                <CardDescription>
                  Contracts expiring within 30 days
                </CardDescription>
              </CardHeader>
              <CardContent>
                {expiringContracts.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">
                    No contracts expiring soon
                  </p>
                ) : (
                  <div className="space-y-2">
                    {expiringContracts.map((contract) => (
                      <div 
                        key={contract.id} 
                        className="p-3 bg-orange-50 border border-orange-200 rounded-lg"
                      >
                        <p className="font-medium">{contract.title}</p>
                        <p className="text-sm text-orange-600">
                          Expires: {contract.contract_expiry}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* AI Workflows Tab */}
        <TabsContent value="workflows" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Natural Language Workflow Creator
              </CardTitle>
              <CardDescription>
                Describe your workflow in plain English and let AI convert it
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Describe your workflow
                </label>
                <textarea
                  className="w-full p-3 border rounded-lg h-32 resize-none"
                  placeholder="Example: When a new invoice is uploaded, it should first go to the Finance team for review. If approved, it goes to the Manager for final approval. If rejected at any stage, notify the uploader. The entire process should complete within 48 hours."
                  value={workflowDescription}
                  onChange={(e) => setWorkflowDescription(e.target.value)}
                  data-testid="workflow-description-input"
                />
              </div>
              
              <Button 
                onClick={handleParseWorkflow} 
                disabled={parsingWorkflow}
                data-testid="parse-workflow-btn"
              >
                {parsingWorkflow ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Convert to Workflow
                  </>
                )}
              </Button>

              {workflowDraft && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-3">Generated Workflow: {workflowDraft.workflow?.name}</h4>
                  
                  {/* Validation Status */}
                  {workflowDraft.validation?.is_valid ? (
                    <div className="flex items-center gap-2 text-green-600 mb-3">
                      <Check className="w-4 h-4" />
                      Workflow is valid
                    </div>
                  ) : (
                    <div className="text-red-600 mb-3">
                      <p className="flex items-center gap-2">
                        <X className="w-4 h-4" />
                        Validation errors:
                      </p>
                      <ul className="list-disc list-inside text-sm">
                        {workflowDraft.validation?.errors?.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Steps Preview */}
                  <div className="space-y-2 mb-4">
                    {workflowDraft.workflow?.steps?.map((step, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded border">
                        <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">
                          {step.step_number}
                        </span>
                        <span className="font-medium">{step.name}</span>
                        <span className="text-sm text-slate-500">
                          ({step.type} - {step.assignee_role})
                        </span>
                        {idx < workflowDraft.workflow.steps.length - 1 && (
                          <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {workflowDraft.validation?.is_valid && (
                    <Button onClick={handleActivateWorkflow} className="bg-green-600 hover:bg-green-700">
                      <Check className="w-4 h-4 mr-2" />
                      Activate Workflow
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Voice Commands Tab */}
        <TabsContent value="voice" className="space-y-4" data-testid="voice-commands-content">
          <CustomCommandsManager headers={getAuthHeaders()} />
        </TabsContent>

        {/* AI Agents Tab */}
        <TabsContent value="agents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-500" />
                AI Agents
              </CardTitle>
              <CardDescription>
                Query specialized AI agents for insights
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Button
                  variant={selectedAgent === 'policy' ? 'default' : 'outline'}
                  onClick={() => setSelectedAgent('policy')}
                  size="sm"
                >
                  Policy Agent
                </Button>
                <Button
                  variant={selectedAgent === 'audit' ? 'default' : 'outline'}
                  onClick={() => setSelectedAgent('audit')}
                  size="sm"
                >
                  Audit Agent
                </Button>
                <Button
                  variant={selectedAgent === 'vendor' ? 'default' : 'outline'}
                  onClick={() => setSelectedAgent('vendor')}
                  size="sm"
                >
                  Vendor Agent
                </Button>
              </div>

              <div className="flex gap-2">
                <Input
                  placeholder={`Ask the ${selectedAgent} agent a question...`}
                  value={agentQuery}
                  onChange={(e) => setAgentQuery(e.target.value)}
                  data-testid="agent-query-input"
                />
                <Button 
                  onClick={handleQueryAgent} 
                  disabled={queryingAgent}
                  data-testid="query-agent-btn"
                >
                  {queryingAgent ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </Button>
              </div>

              {agentResponse && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Brain className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-purple-800">
                      {agentResponse.agent}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      agentResponse.confidence === 'high' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {agentResponse.confidence} confidence
                    </span>
                  </div>
                  <p className="text-slate-700">
                    {agentResponse.answer || JSON.stringify(agentResponse.findings || agentResponse, null, 2)}
                  </p>
                  
                  {agentResponse.recommendations?.length > 0 && (
                    <div className="mt-3">
                      <p className="font-medium text-sm">Recommendations:</p>
                      <ul className="list-disc list-inside text-sm text-slate-600">
                        {agentResponse.recommendations.map((rec, i) => (
                          <li key={i}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Knowledge Tab */}
        <TabsContent value="knowledge" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Network className="w-5 h-5 text-blue-500" />
                Knowledge Graph
              </CardTitle>
              <CardDescription>
                Document relationships and insights
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!knowledgeGraph ? (
                <Button onClick={loadKnowledgeGraph} disabled={loading}>
                  {loading ? 'Loading...' : 'Load Knowledge Graph'}
                </Button>
              ) : (
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center flex-1">
                      <p className="text-2xl font-bold text-blue-600">
                        {knowledgeGraph.node_count}
                      </p>
                      <p className="text-sm text-slate-600">Documents</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center flex-1">
                      <p className="text-2xl font-bold text-purple-600">
                        {knowledgeGraph.edge_count}
                      </p>
                      <p className="text-sm text-slate-600">Relationships</p>
                    </div>
                  </div>
                  
                  {knowledgeGraph.nodes?.length > 0 && (
                    <div className="border rounded-lg p-4 max-h-64 overflow-y-auto">
                      <p className="font-medium mb-2">Connected Documents:</p>
                      {knowledgeGraph.nodes.map((node, idx) => (
                        <div key={idx} className="flex items-center gap-2 py-1">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{node.label}</span>
                          <span className="text-xs px-2 py-0.5 bg-slate-100 rounded">
                            {node.type}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
